import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
} from "@mui/material";
import images from "../../../assets/images";
import {
  OnboardingArrowRightIcon,
  OnboardingMenu1,
  OnboardingMenu2,
  OnboardingMenu3,
  OnboardingMenu4,
} from "../../../assets/icon";
import { motion } from "framer-motion";
import { DotsIndicator } from "../dots-indicator-onboarding/dots-indicator-onboarding";
import { usePreloadImages } from "../../../hooks/usePreloadImages";

interface OverviewOnboardingProps {
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleSkip: () => void;
}

export const OverviewOnboarding = ({
  handlePrevPage,
  handleNextPage,
  handleSkip,
}: OverviewOnboardingProps) => {
  const imagesLoaded = usePreloadImages([images.OnboardingBotSmall]);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isCompactLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 1366px)",
  );
  const isLaptopOrSmaller = useMediaQuery("(max-height: 900px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const checkSize = () => {
      if (contentRef.current && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const containerWidth = containerRef.current.clientWidth;
        const contentHeight = contentRef.current.scrollHeight;
        const contentWidth = contentRef.current.scrollWidth;
        const safetyMargin = isCompactLandscape ? 0.96 : 0.98;
        const heightScale = containerHeight / contentHeight;
        const widthScale = containerWidth / contentWidth;
        const fittedScale = Math.min(heightScale, widthScale) * safetyMargin;
        const minScale = isCompactLandscape
          ? 0
          : isMobile
            ? 0.55
            : isLaptopOrSmaller
              ? 0.6
              : 0.75;
        setScale(Math.max(Math.min(fittedScale, 1), minScale));
      }
    };

    if (imagesLoaded) {
      checkSize();

      window.addEventListener("resize", checkSize);
      window.addEventListener("orientationchange", checkSize);

      const timer = setTimeout(checkSize, 300);

      return () => {
        window.removeEventListener("resize", checkSize);
        window.removeEventListener("orientationchange", checkSize);
        clearTimeout(timer);
      };
    }
  }, [imagesLoaded, isCompactLandscape, isMobile, isLaptopOrSmaller]);

  if (!imagesLoaded) return null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        minHeight: "100dvh",
        width: "100%",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        backgroundColor: "#fff",
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: { xs: "320px", sm: "342px", md: "405px" },
          width: "100%",
          height: "auto",
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          padding: isCompactLandscape
            ? { xs: "10px", sm: "12px" }
            : isLaptopOrSmaller
              ? { xs: "8px", sm: "12px" }
              : { xs: "16px", sm: "20px" },
          paddingBottom: isCompactLandscape
            ? { xs: "40px", sm: "48px" }
            : isLaptopOrSmaller
              ? { xs: "24px", sm: "28px" }
              : { xs: "48px", sm: "56px" },
        }}
      >
        <Box
          component="img"
          loading="eager"
          src={images.OnboardingBotSmall}
          sx={{
            marginTop: isCompactLandscape
              ? { xs: "0px", sm: "8px" }
              : isLaptopOrSmaller
                ? { xs: "0px", sm: "8px" }
                : { xs: "0px", sm: "16px", md: "20px" },
            width: isCompactLandscape
              ? { xs: "64px", sm: "78px" }
              : isLaptopOrSmaller
                ? { xs: "70px", sm: "85px" }
                : { xs: "80px", sm: "100px", md: "140px" },
            height: "auto",
            marginBottom: isCompactLandscape
              ? { xs: "8px", sm: "10px" }
              : isLaptopOrSmaller
                ? { xs: "6px", sm: "8px" }
                : { xs: "12px", sm: "16px" },
          }}
        />

        <Typography
          sx={{
            mb: isCompactLandscape
              ? { xs: "6px", sm: "8px" }
              : isLaptopOrSmaller
                ? { xs: "4px", sm: "6px" }
                : { xs: "8px", sm: "12px" },
            fontSize: isCompactLandscape
              ? { xs: "20px", sm: "24px" }
              : isLaptopOrSmaller
                ? { xs: "22px", sm: "26px" }
                : { xs: "28px", sm: "32px", md: "40px" },
            fontWeight: 700,
            textAlign: "center",
            maxWidth: "480px",
            lineHeight: "110%",
            whiteSpace: { xs: "normal", sm: "nowrap" },
          }}
        >
          <Box component="span" sx={{ color: "#000" }}>
            {"Overview Of"}{" "}
          </Box>
          <Box
            component="span"
            sx={{
              background: "linear-gradient(90deg, #8957CF 0%, #28C9DC 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {"Features"}
          </Box>
        </Typography>
        <Typography
          sx={{
            mb: isCompactLandscape
              ? { xs: "10px", sm: "12px" }
              : isLaptopOrSmaller
                ? { xs: "8px", sm: "10px" }
                : { xs: "16px", sm: "20px" },
            fontSize: isCompactLandscape
              ? { xs: "11px", sm: "13px" }
              : isLaptopOrSmaller
                ? { xs: "12px", sm: "14px" }
                : { xs: "14px", sm: "16px", md: "18px" },
            textAlign: "center",
            color: "#84909F",
            fontWeight: 600,
            maxWidth: { xs: "316px", sm: "316px", md: "382px" },
          }}
        >
          {
            "Connect with me through lively text and voice chats! Dive into personalized interactive lessons and earn exciting badges and certificates as you make progress on your journey!"
          }
        </Typography>
        <Box
          sx={{
            width: "100%",
            gap: isLaptopOrSmaller ? { xs: "4px", sm: "6px" } : { xs: "8px", sm: "12px" },
          }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: isLaptopOrSmaller ? 6 : 8,
            }}
          >
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isLaptopOrSmaller ? "12px" : "16px",
                  padding: isCompactLandscape
                    ? { xs: "3px 6px", sm: "4px 6px" }
                    : isLaptopOrSmaller
                      ? { xs: "3px 5px", sm: "4px 5px" }
                      : { xs: "4px 6px", sm: "6px" },
                  border: "1px solid #E2E5F5",
                  borderRadius: isLaptopOrSmaller ? "12px" : "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: { xs: "36px", sm: "auto" },
                  }}
                >
                  <OnboardingMenu1
                    style={{
                      width: isMobile ? "24px" : isLaptopOrSmaller ? "28px" : "auto",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: isLaptopOrSmaller ? { xs: "13px", sm: "14px" } : { xs: "14px", sm: "16px" },
                  }}
                >
                  Discover & Interact
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isLaptopOrSmaller ? "12px" : "16px",
                  padding: isCompactLandscape
                    ? { xs: "3px 6px", sm: "4px 6px" }
                    : isLaptopOrSmaller
                      ? { xs: "3px 5px", sm: "4px 5px" }
                      : { xs: "4px 6px", sm: "6px" },
                  border: "1px solid #E2E5F5",
                  borderRadius: isLaptopOrSmaller ? "12px" : "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: { xs: "36px", sm: "auto" },
                  }}
                >
                  <OnboardingMenu2
                    style={{
                      width: isMobile ? "24px" : isLaptopOrSmaller ? "28px" : "auto",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: isLaptopOrSmaller ? { xs: "13px", sm: "14px" } : { xs: "14px", sm: "16px" },
                  }}
                >
                  Plan and organize your study
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isLaptopOrSmaller ? "12px" : "16px",
                  padding: isCompactLandscape
                    ? { xs: "3px 6px", sm: "4px 6px" }
                    : isLaptopOrSmaller
                      ? { xs: "3px 5px", sm: "4px 5px" }
                      : { xs: "4px 6px", sm: "6px" },
                  border: "1px solid #E2E5F5",
                  borderRadius: isLaptopOrSmaller ? "12px" : "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: { xs: "36px", sm: "auto" },
                  }}
                >
                  <OnboardingMenu3
                    style={{
                      width: isMobile ? "24px" : isLaptopOrSmaller ? "28px" : "auto",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: isLaptopOrSmaller ? { xs: "13px", sm: "14px" } : { xs: "14px", sm: "16px" },
                  }}
                >
                  Dynamic Courses & Quizzes
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isLaptopOrSmaller ? "12px" : "16px",
                  padding: isCompactLandscape
                    ? { xs: "3px 6px", sm: "4px 6px" }
                    : isLaptopOrSmaller
                      ? { xs: "3px 5px", sm: "4px 5px" }
                      : { xs: "4px 6px", sm: "6px" },
                  border: "1px solid #E2E5F5",
                  borderRadius: isLaptopOrSmaller ? "12px" : "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: { xs: "36px", sm: "auto" },
                  }}
                >
                  <OnboardingMenu4
                    style={{
                      width: isMobile ? "24px" : isLaptopOrSmaller ? "28px" : "auto",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: isLaptopOrSmaller ? { xs: "13px", sm: "14px" } : { xs: "14px", sm: "16px" },
                  }}
                >
                  Certificates & exclusive rewards
                </Typography>
              </Box>
            </motion.div>
          </motion.div>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: isCompactLandscape
              ? { xs: "10px", sm: "12px" }
              : isLaptopOrSmaller
                ? { xs: "8px", sm: "10px" }
                : { xs: "16px", sm: "20px" },
          }}
        >
          <Button
            onClick={handleNextPage}
            sx={{
              fontSize: isCompactLandscape ? { xs: "14px", sm: "16px" } : { xs: "16px", sm: "18px" },
              background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "999px",
              px: isCompactLandscape
                ? { xs: "18px", sm: "24px" }
                : isLaptopOrSmaller
                  ? { xs: "20px", sm: "24px" }
                  : { xs: "24px", sm: "32px" },
              py: isCompactLandscape
                ? { xs: "8px", sm: "10px" }
                : isLaptopOrSmaller
                  ? { xs: "8px", sm: "10px" }
                  : { xs: "12px", sm: "16px" },
              minWidth: { xs: "100px", sm: "auto" },
              boxShadow: "0px 4px 10px rgba(137, 87, 207, 0.2)",
              "&:hover": {
                background: "linear-gradient(90deg, #20B6C8 0%, #7C4ABF 100%)",
              },
            }}
          >
            {"Next"}
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            marginBottom: isLaptopOrSmaller ? { xs: "4px", sm: "6px" } : { xs: "8px", sm: "10px" },
            marginTop: isLaptopOrSmaller ? { xs: "6px", sm: "8px" } : { xs: "10px", sm: "12px" },
            paddingX: { xs: "8px", sm: "12px" },
          }}
        >
          <OnboardingArrowRightIcon
            style={{
              width: isMobile ? "24px" : "30px",
              cursor: "pointer",
            }}
            onClick={handlePrevPage}
          />
          <DotsIndicator activeIndex={1} />
          <Typography
            onClick={handleSkip}
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              color: "#B6BAD5",
              cursor: "pointer",
            }}
          >
            Skip
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
